import { THERAPY_TYPES } from "../../../shared/domain/therapyTypes";

export enum PriceModel {
    FREE = "free",
    SUBSCRIPTION = "subscription",
}

// Therapy types mirror the canonical list in
// modules/session/domain/TherapyTypes.ts (camelCase wire format).
export type TherapyType = (typeof THERAPY_TYPES)[number];

/**
 * Catalogue labels that are not therapy modalities.
 *
 * Kept separate from `TherapyType` so a support tool never shows up in the
 * therapy-type selectors used when creating a session.
 */
export enum ToolCategory {
    ADMINISTRATION = "administration",
    /** An instrument used with a patient, not one that runs the practice. */
    TOOL = "tool",
}

export interface TherapyTool {
    id: string;
    slug: string;
    name: string;
    short_description: string;
    description: string;
    price_model: PriceModel;
    therapy_types: TherapyType[];
    categories?: ToolCategory[];
    icon?: string | null;
    adopted: boolean;
    /**
     * The therapist's centre has switched this tool off. The centre's decision
     * outranks the therapist's: it cannot be adopted from the catalogue, and
     * a previous adoption reads as not adopted until the centre lifts it.
     */
    disabled_by_center?: boolean;
    /**
     * The therapist belongs to a centre, which decides their tools: `adopted`
     * means switched on by the centre, and neither adopting nor removing is
     * theirs to do.
     */
    managed_by_center?: boolean;
    /**
     * What adopting this adds to the subscription, per month, in minor units.
     * Zero — or absent, from an older server — means included in Plus, which
     * is what nearly every tool is.
     */
    monthly_price_cents?: number;
    /**
     * The same module over a year, the annual discount already in it. What an
     * account billed annually is actually charged for adopting it — quoting
     * the monthly figure to somebody on a yearly plan would show 5,00 € and
     * charge 54,00 €.
     *
     * Computed by the server, never here: the discount is a pricing rule, and
     * a copy of it in TypeScript would drift from the provider's price.
     * Absent — an older server — and the monthly figure is all there is.
     */
    annual_price_cents?: number;
    /**
     * When a **dropped** paid module stops being available. `adopted` stays
     * true until then, because it is: money is never given back, so what was
     * paid for is not taken away — what stops is the renewing.
     *
     * Null or absent the rest of the time, which is nearly always.
     */
    access_ends_at?: string | null;
    /**
     * Held because of the plan rather than because somebody pressed adopt: a
     * tool that costs nothing, or any tool at all on an account that is free
     * for ever. There is nothing to press and nothing to remove.
     */
    included_in_plan?: boolean;
    /**
     * The slug of the tool this one comes with, when it is not sold on its
     * own — `constellation` for Genogram & Ecomap. It is held exactly when
     * that tool is, so there is nothing to adopt or remove here.
     */
    included_with?: string | null;
}

/** Whether adopting this tool costs anything on top of the plan. */
export const isPaidTool = (tool: TherapyTool): boolean =>
    (tool.monthly_price_cents ?? 0) > 0;

/** How an account is billed: the period every price here is quoted in. */
export type ToolBillingPeriod = "monthly" | "annual";

/**
 * What adopting this costs, in the period the account is billed in.
 *
 * A module joins the subscription that is already running, so it is charged
 * the way that subscription is: yearly for a yearly plan, monthly for a
 * monthly one. The screen has to say the same figure the card will see.
 *
 * Falls back to the monthly price when a server sends no annual one, which is
 * the honest answer rather than a number worked out here.
 */
export const toolPriceCents = (
    tool: TherapyTool,
    period: ToolBillingPeriod = "monthly"
): number => {
    const monthly = tool.monthly_price_cents ?? 0;
    if (period !== "annual") return monthly;
    return tool.annual_price_cents || monthly;
};

/** Whether a price is being quoted for a year rather than a month. */
export const isAnnualPrice = (
    tool: TherapyTool,
    period: ToolBillingPeriod
): boolean => period === "annual" && (tool.annual_price_cents ?? 0) > 0;

/**
 * The day a dropped module runs out, or null if none is.
 *
 * Parsed here rather than in each component: a screen that showed
 * "Invalid Date" beside somebody's module would be worse than showing nothing.
 */
export const accessEndsAt = (tool: TherapyTool): Date | null => {
    if (!tool.access_ends_at) return null;
    const parsed = new Date(tool.access_ends_at);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export interface MarketplaceFilters {
    therapyType?: TherapyType;
    priceModel?: PriceModel;
    category?: ToolCategory;
    search?: string;
}

/** Every catalogue label, in the order the filter offers them. */
export const TOOL_CATEGORIES: ToolCategory[] = Object.values(ToolCategory);

/**
 * What a member's centre has decided about a tool.
 *
 * Three answers, because "switched off" and "never switched on" are different
 * things to say: a module the centre never bought is not one it took away.
 * Reading both as "enabled" is how AlchelyMeeting came to say "enabled by
 * your centre" on a card whose feature was nowhere to be found.
 */
export type CenterToolState = "enabled" | "disabled" | "notEnabled";

export const centerState = (tool: TherapyTool): CenterToolState => {
    if (tool.disabled_by_center) return "disabled";
    return tool.adopted ? "enabled" : "notEnabled";
};
